import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faTags } from "@fortawesome/free-solid-svg-icons";
import ContentContainer from "templates/content-container/contentContainer";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import ReloadButton from "ui-components/reload-button/reloadButton";
import { PAGE_IDS } from "app/data/common/pageIds";
import { userSelector } from "slices/user/userSlice";
import { DockEmployee } from "app/data/dock-scan/models";
import { useDockOperationsSummary } from "./hooks/useDockScanQueries";
import { useDockUsersRefresh } from "./hooks/useDockUsersRefresh";
import DockOperationsSummaryTiles from "./components/DockOperationsSummaryTiles";
import DockEmployeesTab from "./components/DockEmployeesTab";
import DockTagsTab from "./components/DockTagsTab";
import DockEmployeeSidebar from "./components/DockEmployeeSidebar";
import CreateDockEmployeeSidebar from "./components/CreateDockEmployeeSidebar";
import "features/shipment-reconciliation/revenue-insights/accessorialRevenueInsights.scss";
import "./dockUsers.scss";

export type DockUsersViewMode = "employees" | "tags";

const VIEW_OPTIONS: { id: DockUsersViewMode; label: string; icon: typeof faUsers }[] = [
  { id: "employees", label: "Employees", icon: faUsers },
  { id: "tags", label: "Tags", icon: faTags },
];

const DockUsersList: React.FC = () => {
  const userState = useSelector(userSelector);
  const location = useLocation();
  const terminalNumber = userState.activeTerminal?.id
    ? Number(userState.activeTerminal.id)
    : undefined;
  const terminalName = userState.activeTerminal?.name || "Terminal";

  const [viewMode, setViewMode] = useState<DockUsersViewMode>("employees");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>();
  const [showEmployeeSidebar, setShowEmployeeSidebar] = useState(false);
  const [showCreateSidebar, setShowCreateSidebar] = useState(false);

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useDockOperationsSummary(terminalNumber);

  const {
    refreshAll,
    isRefreshing,
    lastRefreshedLabel,
    setLastRefreshedAt,
  } = useDockUsersRefresh(terminalNumber);

  useEffect(() => {
    if (terminalNumber != null && summary) {
      setLastRefreshedAt(Date.now());
    }
  }, [terminalNumber, summary, setLastRefreshedAt]);

  const handleSelectEmployee = useCallback((employee: DockEmployee) => {
    setSelectedEmployeeId(employee.employeeId);
    setShowEmployeeSidebar(true);
  }, []);

  useEffect(() => {
    const employeeId = new URLSearchParams(location.search).get("employeeId");
    if (employeeId) {
      setSelectedEmployeeId(employeeId);
      setShowEmployeeSidebar(true);
      setViewMode("employees");
    }
  }, [location.search]);

  if (!terminalNumber) {
    return (
      <ContentContainer
        className="xgs-list xgs-list--full-width"
        mods={{ "full-width": true }}
        titleComponent={
          <ContentContainerToolbar
            pageId={PAGE_IDS.DOCK_USERS}
            title="Dock Users"
            mods={{ "full-width": true }}
          />
        }
      >
        <div className="dock-users__empty">Select a terminal to manage dock users.</div>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.DOCK_USERS}
          title={`Dock Users — ${terminalName}`}
          mods={{ "full-width": true }}
        >
          <div className="dock-users__refresh-toolbar">
            <span className="dock-users__last-refreshed">
              Last refreshed {lastRefreshedLabel ?? "—"}
            </span>
            <ReloadButton
              title="Refresh"
              spinner={isRefreshing}
              disabled={isRefreshing}
              onClick={() => void refreshAll()}
            />
            <div className="dock-users__view-switcher" role="group" aria-label="Dock users view">
              {VIEW_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`dock-users__view-btn${viewMode === opt.id ? " dock-users__view-btn--active" : ""}`}
                  title={opt.label}
                  aria-label={opt.label}
                  aria-pressed={viewMode === opt.id}
                  onClick={() => setViewMode(opt.id)}
                >
                  <FontAwesomeIcon icon={opt.icon} aria-hidden />
                </button>
              ))}
            </div>
          </div>
        </ContentContainerToolbar>
      }
    >
      <DockOperationsSummaryTiles
        summary={summary}
        isLoading={summaryLoading}
        isError={summaryError}
      />

      {viewMode === "employees" && (
        <DockEmployeesTab
          terminalNumber={terminalNumber}
          terminalName={terminalName}
          onSelectEmployee={handleSelectEmployee}
          onCreateEmployee={() => setShowCreateSidebar(true)}
        />
      )}
      {viewMode === "tags" && <DockTagsTab terminalNumber={terminalNumber} />}

      <DockEmployeeSidebar
        show={showEmployeeSidebar}
        employeeId={selectedEmployeeId}
        terminalNumber={terminalNumber}
        onClose={() => {
          setShowEmployeeSidebar(false);
          setSelectedEmployeeId(undefined);
        }}
      />

      <CreateDockEmployeeSidebar
        show={showCreateSidebar}
        terminalNumber={terminalNumber}
        onClose={() => setShowCreateSidebar(false)}
        onCreated={(employeeId) => {
          setSelectedEmployeeId(employeeId);
          setShowEmployeeSidebar(true);
        }}
      />
    </ContentContainer>
  );
};

export default DockUsersList;
